import React from "react";
import PropTypes from "prop-types";
import { FaArrowLeft, FaExclamationCircle } from "react-icons/fa";
import OtpCodeInput from "../login/OtpCodeInput";
import {
  OTP_CODE_LENGTH,
  SIGNUP_OTP_ERROR_ID
} from "../../utils/constants/appConstants";
import { signup } from "../../locales/en";

/**
 * The screen shown after the signup form is submitted: confirm the code that
 * has just been emailed, which is what creates the account.
 *
 * It replaces the form rather than expanding underneath it, so the only thing
 * being asked for is the code. The form itself is still mounted behind this
 * and keeps its values, which is what lets "Change email address" return to a
 * filled-in form rather than an empty one.
 */
const EmailVerificationStep = ({
  email,
  code,
  onCodeChange,
  error,
  resent,
  sending,
  verifying,
  creating,
  onVerify,
  onResend,
  onBack
}) => {
  const copy = signup?.emailOtp ?? {};

  // Sending, checking and creating all act on the same server-side code, so
  // every control locks while any of them is in flight: a resend retires the
  // code an in-flight check is verifying, which would land an "expired"
  // rejection on boxes the resend has just cleared.
  const busy = sending || verifying || creating;

  const confirmLabel = () => {
    if (creating) {
      return copy.creating;
    }
    return verifying ? copy.verifying : copy.verifyButton;
  };

  return (
    <div className="auth-otp-step">
      <p className="auth-otp-sent-to">
        {copy.subheading} <strong>{email}</strong>
      </p>

      <label className="auth-otp-label" htmlFor="otp-code-0">
        {copy.label}
      </label>
      <OtpCodeInput
        length={OTP_CODE_LENGTH}
        value={code}
        error={Boolean(error)}
        disabled={busy}
        ariaLabel={copy.label}
        describedBy={error ? SIGNUP_OTP_ERROR_ID : undefined}
        onChange={onCodeChange}
      />

      {error && (
        <p className="error-message" id={SIGNUP_OTP_ERROR_ID} role="alert">
          <FaExclamationCircle className="error-icon" aria-hidden />
          <small>{error}</small>
        </p>
      )}
      {resent && !error && (
        <p className="error-message text-success" role="status">
          <small>{resent}</small>
        </p>
      )}

      <button
        type="button"
        className={`auth-btn-verify is-primary${
          code.length === OTP_CODE_LENGTH ? " is-ready" : ""
        }`}
        onClick={onVerify}
        disabled={busy}
      >
        {creating && <span className="auth-spinner" aria-hidden="true" />}
        {confirmLabel()}
      </button>

      <div className="auth-otp-step-actions">
        <button
          type="button"
          className="auth-btn-resend"
          onClick={onResend}
          disabled={busy}
        >
          {sending ? copy.resending : copy.resend}
        </button>
        <button
          type="button"
          className="auth-btn-back"
          onClick={onBack}
          disabled={busy}
        >
          <FaArrowLeft aria-hidden /> {copy.changeEmail}
        </button>
      </div>
    </div>
  );
};

EmailVerificationStep.defaultProps = {
  email: "",
  code: "",
  error: "",
  resent: "",
  sending: false,
  verifying: false,
  creating: false
};

EmailVerificationStep.propTypes = {
  email: PropTypes.string,
  code: PropTypes.string,
  onCodeChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  resent: PropTypes.string,
  sending: PropTypes.bool,
  verifying: PropTypes.bool,
  creating: PropTypes.bool,
  onVerify: PropTypes.func.isRequired,
  onResend: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired
};

export default EmailVerificationStep;
